import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import useUnreadMessagesContext from '@contexts/unreadMessagesContext'
import { isAvatarSrc, thumbnailMediumBlue } from '@utils/thumbnails'
import useConversationsContext from '@contexts/conversationsContext'
import { useLocation, useSearchParams } from 'react-router-dom'
import useSnackBarContext from '@contexts/snackBarContext'
import useInfiniteScroll from '@hooks/useInfiniteScroll'
import { useAuthContext } from '@contexts/authContext'
import useModalContext from '@contexts/modalContext'
import useCableContext from '@contexts/cableContext'
import searchParamsHas from '@utils/searchParamsHas'
import { MapView } from '@contexts/mapDataContext'
import useLoading from '@hooks/useLoading'
import axios from '@utils/webRequests'
import { size } from '@styles/theme'
import styled from '@emotion/styled'
import guid from '@utils/guid'

import { Button, CircularProgress, IconButton } from '@mui/material'
import MessagesInputBar from '@components/messages/MessagesInputBar'
import MessagesTopbar from '@components/messages/MessagesTopbar'
import ModalContainer from '@elements/ModalContainer'
import Message from '@components/messages/Message'
import { PiCaretDownBold } from 'react-icons/pi'
import useStateRef from 'react-usestateref'

const OuterContainer = styled.div`
  height: 100dvh;
  width: 100dvw;
  overflow: hidden;
  position: relative;
`

const ScrollArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column-reverse;
  height: 100%;
  width: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
`

const ClearfixTop = styled.div`
  flex-shrink: 0;
  height: 150px;
  width: 100%;
`

const ClearfixBottom = styled.div`
  flex-shrink: 0;
  height: ${size(7.5)};
  width: 100%;
`

const Messages = () => {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { CableEmitter } = useCableContext()
  const { state: authState } = useAuthContext()
  const { userReadMessages } = useUnreadMessagesContext()
  const { addSnackBar } = useSnackBarContext()
  const { messagesModal } = useModalContext()
  const [messages, setMessages] = useState([])
  const [otherUser, setOtherUser, otherUserRef] = useStateRef(null)
  const [conversation, setConversation, conversationRef] = useStateRef(null)
  const [hasMoreMessages, setHasMoreMessages] = useState(true)
  const { setScrollRef, hitThreshold, scrollRef } = useInfiniteScroll(100)
  const [aboveViewportEl, setAboveViewportEl] = useState(null)
  const [isScrollAboveViewport, setIsScrollAboveViewport] = useState(false)
  const [hasNewMessage, setHasNewMessage] = useState({ isTrue: false, at: null })
  const initialViewPortHeight = useRef(window.visualViewport.height)
  const [isNewConversation, setIsNewConversation, isNewConversationRef] = useStateRef(false)
  const { addConversationToAll, addConversationToFavorites, removeFromFavoritesAndUpdateInAll, sendConversationToTop, updateMessageReadStatus } = useConversationsContext()
  const { isLoading: isLoadingConversation, isLoadingDelayed: isLoadingConversationDelayed, setIsLoading: setIsLoadingConversation, } = useLoading(false, 1000)
  const { isLoading: isLoadingInitialMessages, isLoadingDelayed: isLoadingInitialMessagesDelayed, setIsLoading: setIsLoadingInitialMessages, } = useLoading(false, 1000)
  const { isLoading: isLoadingMoreMessages, isLoadingDelayed: isLoadingMoreMessagesDelayed, setIsLoading: setIsLoadingMoreMessages, } = useLoading(false, 500)
  const isAvatar = isAvatarSrc('medium', otherUser?.nearbyMedia?.thumbnails?.[0], otherUser?.defaultAvatar)
  const userThumbnail = thumbnailMediumBlue(otherUser?.nearbyMedia?.thumbnails?.[0], otherUser?.defaultAvatar)
  
  // NOTE: We're parsing the conversation id this way because useParams ONLY parses route paramters of react-router dom.
  // NOTE: We could use searchParams and should probably refactor to that
  let conversationId = searchParams.get('conversation_id')
  if (conversationId === 'null') conversationId = null;

  // This is the logic to run when the modal gets closed
  useEffect(() => {
    // Reset state on "unmount"
    if (!messagesModal.isOpen) {
      // Wer'e using the conversation.id, because the conversationId has been removed from the url by this time,
      // but the actual conversation hasn't been removed from the state yet
      const currentConversationId = (conversation?.uuid || conversationId)
      if (messages.length > 0 && currentConversationId) {
        localStorage.setItem(`messages-${currentConversationId}`, JSON.stringify(messages))
      }
      setMessages([])
      setHasMoreMessages(true)
      setIsLoadingConversation(false)
      setIsLoadingInitialMessages(false)
      setIsLoadingMoreMessages(false)
      setOtherUser(null)
      setConversation(null)
      setIsNewConversation(false)
    }
  }, [messagesModal.isOpen])

  // Toggles unread message notification icon off in bottom bar when user views messages from conversation screen
  // NOTE: We don't want to toggle these off if the user views the messages from a profile screen.
  useEffect(() => {
    const isViewingconversationList = window?.location?.pathname?.includes?.('/conversations')
    if (messagesModal.isOpen && isViewingconversationList) {
      // NOTE: The message is added to the seenMessages when we receive the data.
      // We JUST need to toggle the notification icon off when the user views the messages screen.
      userReadMessages()
    }
  }, [messagesModal.isOpen])

  // This handles the logic around the scroll to bottom button
  useEffect(() => {
    if (!aboveViewportEl) return;
    const observer = new window.IntersectionObserver(([entry]) => {
      // console.log(entry.boundingClientRect.top)
      if (entry.isIntersecting) {
        // ANCHOR IS VISIBLE IN VIEWPORT
        setIsScrollAboveViewport(false)
        setHasNewMessage((hasNewMessage) => ({
          ...hasNewMessage,
          isTrue: false,
        }))
        return
      }
      if (entry.boundingClientRect.top > 0) {
        // IS BELOW ANCHOR
        setIsScrollAboveViewport(true)
      } else {
        // IS ABOVE ANCHOR
        setIsScrollAboveViewport(false)
        setHasNewMessage((hasNewMessage) => ({
          ...hasNewMessage,
          isTrue: false,
        }))
      }
    }, {
      root: null,
      threshold: 0,
    })
    observer.observe(aboveViewportEl)
  }, [aboveViewportEl])

  // retrieve more messages when we hit a certain scroll threshold up
  // TODO: maybe try not to fire this if the page loads wihtin the threshold to start
  useEffect(() => {
    if (hitThreshold && hasMoreMessages) {
      getMessages({ offset: messages?.length || 0, replace: false, requestType: 'more' })
    }
  }, [hitThreshold])
  
  // this effect handles what should happen when the modal opens from a place where we only have the other user, and ourselves (like the map or social profile)
  useLayoutEffect(() => {
    if (messagesModal.isOpen && !conversationId && location.state?.user) {
      const { user: newUser } = location.state
      setOtherUser(newUser)
      const otherUserId = newUser?.nearbyUuid
      setIsLoadingConversation(true)
      axios.post(`/api/my/conversations`, { other_user_id: otherUserId })
        .then((res) => {
          // WARNING: If we set the search params after the screen has changed, this will cause the modal
          //          to pop back up incorrectly with bad data. This typically happens when the user backnavigates
          //          before this request can be returned. Hence the need to short circuit.
          if (!searchParamsHas('screen_modal', 'messages')) return;
          if (res.data.uuid) {
            searchParams.set('conversation_id', res.data.uuid)
            attemptToSetMessagesFromState(res.data.uuid)
          }
          setIsLoadingConversation(false)
          setConversation(res.data)
          setIsNewConversation(res.data.isNew)
          setSearchParams(searchParams, { replace: true })
        })
        .catch((err) => console.log(err))
    } else {
      // TODO: I'm not entirely sure if this and the unmount are being called together... Or really on what conditions the unmount function is being called at all.
      //       So we need to revisit these.
      setMessages([])
      setIsNewConversation(false)
    }
  }, [messagesModal.isOpen])


  // This handles getting the conversation information when we have the conversation id, (typically navigate from the conversations screen)
  useEffect(() => {
    if (!messagesModal.open || conversation || !conversationId || !authState?.user) return;
    if (location.state?.user && location.state.conversation) {
      setOtherUser(location.state.user)
      setConversation(location.state.conversation)
    }
    attemptToSetMessagesFromState()
    // NOTE: Since we already have the conversation details (theoretically) we don't need to activate the conversation loader and block messaging
    axios.get(`/api/my/conversations/${conversationId}`)
      .then((res) => {
        setConversation(res.data)
        // NOTE: we're getting all the users in the conversation from the backend, an that's why we need to parse out the other user this way
        setOtherUser(res.data?.users?.find((u) => u.uuid !== authState?.user?.uuid))
      })
      .catch(console.log)
  }, [messagesModal.isOpen, authState?.user])

  // This gets messages when we have the conversation id, or when it changes
  useLayoutEffect(() => {
    if (!conversationId) return;
    else {
      getMessages({ replace: true, offset: 0, requestType: 'initial' })
        .catch(() => {
          console.log('failed to retreive messages')
          // try { setMessages(JSON.parse(localStorage.getItem(`messages-${conversationId}`) || '[]')) }
          // catch { console.log('Couldn\'t parse message JSON') }
        })
    }
  }, [conversationId, authState?.user])

  // Add listener for MessageChannel events on initial render
  useEffect(() => {
    const callbackId = CableEmitter.addEventListener('UserMessage', (data) => {
      if (data?.message?.conversationUuid === conversationId) {
        if (data?.action === 'create' || data?.action === 'create:conversation') {
          data.message.isMetaDataOpen = true
          if (isNewConversationRef.current) {
            setIsNewConversation(false)
          }
          setHasNewMessage((oldNewMessageState) => {
            if (!oldNewMessageState?.isTrue) {
              return {
                isTrue: true,
                at: isScrollAboveViewport ? Date.parse(data?.message?.createdAt || new Date()) : null
              }
            } else return oldNewMessageState
          })
          setMessages((oldMessages) => {
            if (oldMessages.length > 1) {
              // Closes the meta data of the previous message if we get a new one
              oldMessages[0].isMetaDataOpen = false
            }
            const newData = [data.message, ...oldMessages]
            return newData
          })
        } else if (data?.action === 'update') {
          setMessages((oldMessages) => {
            return oldMessages.map((oldMessage) => {
              if (oldMessage.uuid === data?.message?.uuid) {
                return data?.message
              } else {
                return oldMessage
              }
            })
          }) 
        } else if (data?.action === 'destroy') {
          setMessages((oldMessages) => {
            return oldMessages.map((oldMessage) => {
              if (oldMessage.uuid === data?.message?.uuid) {
                oldMessage.isDestroyed = true
              }
              return oldMessage
            })
          }) 
        } else if (data?.action === 'destroy:image') {
          setMessages((oldMessages) => {
            return oldMessages.map((oldMessage) => {
              if (oldMessage.uuid === data?.message?.uuid) {
                oldMessages.images = oldMessage.images.map((image) => {
                  if (image.uuid === data?.destroyed_image?.uuid) {
                    image.isDestroyed = true
                  }
                  return image
                })
              }
              return oldMessage
            })
          }) 
        }
      }
    })
    return () => CableEmitter.removeEventListener('UserMessage', callbackId)
  }, [conversationId, isScrollAboveViewport])

  const attemptToSetMessagesFromState = (dynamicConversationId) => {
    try {
      const accessor = `messages-${dynamicConversationId || conversationId}`
      let messages = JSON.parse(localStorage.getItem(accessor) || null)
      if (Array.isArray(messages) && messages.length > 0) {
        setMessages(messages)
      }
    } catch {}
  }

  const closeMetaData = (message) => {
    const newMessageState = messages.map((oldMessage) => {
      if (message?.uuid && oldMessage.uuid === message?.uuid) {
        oldMessage.isMetaDataOpen = false
        return oldMessage
      } else if (message?.fakeId && oldMessage?.fakeId === message?.fakeId) {
        oldMessage.isMetaDataOpen = false
        return oldMessage
      } else {
        return oldMessage
      }
    })
    setMessages(newMessageState)
  }

  const closeAllMetaData = (messages) => {
    return messages.map((message) => {
      message.isMetaDataOpen = false
      return message
    })
  }

  const openMetaData = (message) => {
    const newMessageState = messages.map((oldMessage) => {
      if (message?.uuid && oldMessage.uuid === message?.uuid) {
        oldMessage.isMetaDataOpen = true
        return oldMessage
      } else if (message?.fakeId && oldMessage?.fakeId === message?.fakeId) {
        oldMessage.isMetaDataOpen = true
        return oldMessage
      } else {
        return oldMessage
      }
    })
    setMessages(newMessageState)
  }

  const addMetaStateVar = (messages) => messages.map((message, index) => {
    if(!message?.hasOwnProperty('isMetaDataOpen')) {
      if(index === 0) {
        message.isMetaDataOpen = true
      } else {
        message.isMetaDataOpen = false
      }
    } 
    return message
  })

  function getMessages(options={ offset: 0, replace: false, requestType: 'initial' }) {
    return new Promise((resolve, reject) => {
      if (!conversationId) return;
      const setIsLoadingFunc = (options.requestType === 'initial') ? setIsLoadingInitialMessages : setIsLoadingMoreMessages
      setIsLoadingFunc(true)
      const reqOptions = { params: { offset: options?.offset || 0 } }
      axios.get(`/api/my/conversations/${conversationId}/messages`, reqOptions)
        .then((res) => {
          setIsLoadingFunc(false)
          if (res?.data?.length < 100) setHasMoreMessages(false)
          if (options.replace) {
            setMessages(addMetaStateVar(res.data || []))
          } else {
            setMessages(messages.concat(addMetaStateVar(res.data || [])))
          }
          // We're updating the message read status here because we need access to the actual messages rather than the unread index
          // This means we can't do it globally until the messages are stored globally.
          if (res?.data?.length > 0) {
            if (conversationRef.current && otherUserRef.current)  {
              const otherUserUuid = otherUserRef.current.nearbyUuid
              updateMessageReadStatus(conversationRef.current, otherUserUuid);
            }
            axios.put(`api/my/conversations/${conversationId}/unread_messages`)
              .catch(console.log)
          }
          resolve(res.data)
        })
        .catch((err) => {
          setIsLoadingFunc(false)
          console.log(err)
          reject(err)
        })
    })
  }

  const destroyMessage = (message) => {
    const newData = messages.map((oldMessage) => {
      if(oldMessage?.uuid === message?.uuid) {
        oldMessage.isDestroyed = true
      }
      return oldMessage
    })
    setMessages(newData)
    axios.delete(`/api/my/conversations/${conversationId}/messages/${message?.uuid}`)
      .then(console.log)
      .catch(console.log)
  }

  const destroyImage = (message, image) => {
    const newData = messages.map((oldMessage) => {
      if (oldMessage?.uuid === message?.uuid) {
        oldMessage.images = oldMessage.images.map((oldImage) => {
          if (oldImage.uuid === image.uuid) {
            oldImage.isDestroyed = true
          }
          return oldImage
        })
      }
      return oldMessage
    })
    setMessages(newData)
    axios.delete(`/api/my/messages/${message?.uuid}/images/${image?.uuid}`)
      .then(console.log)
      .catch(console.log)
  }

  const addFakeReactionToMessage = (parent, emoji) => {
    const newData = messages.map((message) => {
      if (message?.uuid === parent?.uuid) {
        message.messageReactions = message.messageReactions.concat({ emoji, userUuid: authState?.user?.uuid })
      }
      return message
    })
    setMessages(newData)
  }
  
  const addFakeReactionToImage = (parent, emoji) => {
    const newData = messages.map((message) => {
      if (message?.images.length > 0) message?.images.map((image) => {
        if (image?.uuid === parent?.uuid) {
          image.messageReactions = image.messageReactions.concat({ emoji, userUuid: authState?.user?.uuid })
        }
        return image
      })
      return message
    })
    setMessages(newData)
  }

  const createMessageReaction = (parentType, parent, emoji) => {
    // We update data immediately to see an instant response and replace the message after via websocket update
    if (parentType === 'Message') addFakeReactionToMessage(parent, emoji)
    else if (parentType === 'Image') addFakeReactionToImage(parent, emoji)
    const data = {
      parent_id: parent?.uuid,
      parent_type: parentType,
      emoji,
    }
    return axios.post(`/api/my/conversations/${conversationId}/message_reactions`, data)
      .then(console.log)
      .catch(console.log)
  }

  const removeReactionFromMessage = (parent, reactionUuid) => {
    const newData = messages.map((message) => {
      if (message?.uuid === parent?.uuid) {
        message.messageReactions = message
          ?.messageReactions
          ?.filter((reaction) => reaction?.uuid !== reactionUuid)
      }
      return message
    })
    setMessages(newData)
  }

  const removeReactionFromImage = (parent, reactionUuid) => {
    const newData = messages.map((message) => {
      if (message?.images.length > 0) message?.images.map((image) => {
        if (image?.uuid === parent?.uuid) {
          image.messageReactions = image
            ?.messageReactions
            ?.filter((reaction) => reaction?.uuid !== reactionUuid)
        }
        return image
      })
      return message
    })
    setMessages(newData)
  }

  const destroyMessageReaction = (parentType, parent, reactionUuid) => {
    // We update data immediately to see an instant response and replace the message after via websocket update
    if (parentType === 'Message') removeReactionFromMessage(parent, reactionUuid)
    else if (parentType === 'Image') removeReactionFromImage(parent, reactionUuid)
    const params = {
      parent_id: parent?.uuid,
      parent_type: parentType,
    }
    return axios.delete(`/api/my/conversations/${conversationId}/message_reactions/${reactionUuid}`, { params, })
      .then(console.log)
      .catch(console.log)
  }

  const toggleFavorite = () => {
    if (!conversation || conversation?.isLoading) return;
    // Update the favorite state immediately and set to loading
    setConversation({ ...conversation, isFavorite: !conversation.isFavorite, isLoading: true })
    // send off the request and replace the conversation when it returns
    const action = conversation.isFavorite ? 'delete' : 'post'
    axios[action](`/api/my/conversations/${conversation?.uuid}/favorites`)
      .then((res) => {
        if (res?.data?.isFavorite) {
          addConversationToFavorites(res.data)
        } else {
          removeFromFavoritesAndUpdateInAll(res.data.uuid)
        }
        setConversation({ ...res.data, isLoading: false })
      })
      .catch((err) => {
        // TODO: Add some sort of failure prompt here
        console.log(err)
        addSnackBar({ message: 'Sorry, we couldn\'t favorite this conversation', kind: 'error' })
        setConversation({ ...conversation, isLoading: false })
      })
  }

  const addNewMessageAfterSend = (messagesSent) => {
    const newMessages = Array.isArray(messagesSent) ? [...messagesSent] : [messagesSent]
    setMessages(newMessages.concat(closeAllMetaData(messages)))
    setHasNewMessage({ isTrue: false, at: null })
    if (otherUser) {
      MapView.setUserHasSentMessage(otherUser.nearbyUuid, authState?.user?.nearbyUuid)
    }
  }

  const handleConversationNewMessageSent = (mostRecentMessage) => {
    if (isNewConversation) {
      addConversationToAll({ ...conversation, mostRecentMessage, })
      setIsNewConversation(false)
    } else {
      sendConversationToTop({ ...conversation, mostRecentMessage, }, true)
    }
  }

  const handleSubmit = async (newMessage) => {
    if (!conversationId || isLoadingConversation || isLoadingInitialMessages) return;
    // Here we set the message to the new state right away so the user doesn't get a delay
    // The point of setting the fake ID is to have an identifier we can use to replace the state later once the message
    // gets sent from the backend. In the meantime we can display a little sending icon at the bottom
    const fakeId = guid()
    const fakeMessage = {
      userUuid: authState?.user?.uuid,
      user: authState?.user,
      body: newMessage,
      isMetaDataOpen: true,
      fakeCreatedAt: new Date(),
      fakeId,
    }
    // Add the fake message to the data to be replaced when the real one comes back
    addNewMessageAfterSend(fakeMessage)
    // Create Form Data for request (necessary for file)
    const messageData = { body: newMessage }
    messageRequest(conversationId, messageData, fakeId)
  }

  const messageRequest = (conversationId, formData, fakeId) => {
    axios.post(`/api/my/conversations/${conversationId}/messages`, formData)
      .then((res) => {
        handleConversationNewMessageSent(res.data)
        setMessages((oldMessages) => {
          return replaceFakeMessage(oldMessages, res.data, fakeId)
        })
      })
      .catch(console.log)
  }

  const replaceFakeMessage = (oldMessages, message, fakeId) => {
    message.isMetaDataOpen = true
    return oldMessages.map((oldMessage) => {
      if (oldMessage?.fakeId === fakeId) {
        // WARNING: We need to keep the fakeId we were using before, because react will unmount/mount
        //          The old message when it replaces the key that's being used in the list. This is causing
        //          some very weird flickering from the entrance animations, and it's best avoided by keeping
        //          old key the same until the data is re-fetched.
        message.fakeId = oldMessage.fakeId
        return message
      } else {
        return oldMessage
      }
    })
  }

  const renderLoadText = () => {
    let loadText = ''
    if (isLoadingConversationDelayed) {
      loadText = 'checking conversation'
    } else if (isLoadingInitialMessagesDelayed) {
      loadText = 'checking messages'
    }
    return loadText
  }

  //NOTE: The 'Next' message is closer to the bottom, while the 'Previous' message is closer to the top
  const renderMessages = () => messages?.filter((message) => message?.body || (Array.isArray(message?.media) && message?.media?.length > 0) || message?.fakeCreatedAt)?.map((message, index, messageArr) => (
    <Message
      nextMessage={(index - 1 >= 0) && messageArr[index - 1]}
      prevMessage={(index + 1 < messages.length) && messageArr[index + 1]}
      message={message}
      newMessageAt={hasNewMessage?.at}

      userThumbnail={userThumbnail}
      isAvatar={isAvatar}

      // WARNING: We need to use the fakeId so that when the new data doesn't come back, we can keep the key the same
      //          thus preventing react from unmounting and remounting the component, which causes animation flickers.
      key={message?.fakeId || message?.uuid}
      index={index}
      closeMetaData={closeMetaData}
      openMetaData={openMetaData}
      destroyMessage={destroyMessage}
      destroyImage={destroyImage}

      createMessageReaction={createMessageReaction}
      destroyMessageReaction={destroyMessageReaction}
    />
  ))

  return (
    <ModalContainer noHeader open={messagesModal.isOpen}>
      <MessagesTopbar
        toggleFavorite={toggleFavorite}
        conversation={conversation}
        otherUser={otherUser}
        isNewConversation={isNewConversation}
        userThumbnail={userThumbnail}
        isAvatar={isAvatar}
      />
      <OuterContainer>
        <ScrollArea
          ref={node => node && setScrollRef(node)}>
          <div ref={node => node && setAboveViewportEl(node)} style={{ position: 'absolute', top: initialViewPortHeight?.current ? -(initialViewPortHeight?.current/2) : 0, left: '0' }} />
          {isScrollAboveViewport && !hasNewMessage?.isTrue &&
            <IconButton
              onClick={() => scrollRef?.scroll({ top: 0, behavior: 'smooth' })}
              style={{
                // backgroundColor: '#31303d',
                backgroundColor: '#474747',
                color: 'rgb(235,235,235)',
                boxShadow: '0 1px 8px rgba(0,0,0,0.25)',
                position: 'fixed',
                bottom: size(9),
                right: size(0),
                transform: 'translateX(-50%)',
                zIndex: 1,
                animation: 'growInCenteredX 200ms',
              }}>
              <PiCaretDownBold size={size(3)} fill='white' />
            </IconButton>
          }
          {isScrollAboveViewport && hasNewMessage?.isTrue &&
            <Button
              onClick={() => scrollRef?.scroll({ top: 0, behavior: 'smooth' })}
              style={{
                backgroundColor: '#7246cf',
                color: 'rgb(235,235,235)',
                boxShadow: '0 1px 8px rgba(0,0,0,0.25)',
                position: 'fixed',
                bottom: size(9),
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                borderRadius: '100px',
                animation: 'growInCenteredX 200ms',
              }}>
              <img src={otherUser?.thumbnail} style={{ borderRadius: '100%', objectFit: 'cover', height: size(4), width: size(4), marginRight: size(0.5) }} />
              <span>New Message</span>
            </Button>
          }
          <ClearfixBottom style={{}} />
          {messages.length > 0 && renderMessages()}
          {isLoadingMoreMessagesDelayed && (
            <div style={{ display: 'flex', width: '100%', justifyContent: 'center', alignItems: 'center' }}>
              <CircularProgress style={{ margin: '1rem', color: '#6A23FF' }} thickness={8} />
            </div>
          )}
          {isNewConversation && (
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', justifyContent: 'flex-start', alignItems: 'center' }}>
              <h3 style={{ color: 'white' }}>Start a new conversation 😄</h3>
            </div>
          )}
          <ClearfixTop />
        </ScrollArea>
      </OuterContainer>
      <MessagesInputBar
        loadText={renderLoadText()}
        disabled={!conversationId || isLoadingConversation || isLoadingInitialMessages}
        handleSubmit={handleSubmit}
        handleConversationNewMessageSent={handleConversationNewMessageSent}
        addNewMessageAfterSend={addNewMessageAfterSend}
      />
    </ModalContainer>
  )
}

export default Messages
